// 🎙 회의 녹음·회의록 (2026-10-26 형 1단계) — 서버 API + 녹음 엔진(한 번에 하나).
//   · MediaRecorder 30초 조각 → /api/tpt/meetrec/<id>/chunk?seq=N 순서대로 올림(실패 재시도, 같은 번호 재전송 멱등).
//   · 종료 = 남은 조각을 다 올린 뒤 stop → 서버 잡이 받아쓰기·회의록·방 게시.
//   · 안드 앱: TPTAndroid.setRecording(on, title, rid) 가 있으면 마이크 전경 서비스로 화면이 꺼져도 녹음 유지(앱 code 127+).
//   · 시작·종료 음성 안내(api/tts speak) — 형 "녹음은 표시 및 음성표시까지".
//   인증 헤더는 installTptAuthFetch 가 /api/tpt 요청에 붙인다.
import { useSyncExternalStore } from 'react';
import { speak } from './tts';
import type { LangCode } from './translate';
import { isIosApp } from '../utils/platform';

export const MEET_REC_FLAG = 'meet-recorder';
export const MEET_REC_START_EVENT = 'tpt:meetrec-start';   // detail: MeetRecStartReq
export const MEET_REC_OPEN_EVENT = 'tpt:meetrec-open';     // detail: { id?: string }
export const MEET_REC_STOP_REQUEST = 'tpt:meetrec-stop-request';   // 안드 알림 [녹음 끝내기]

export interface MeetRecStartReq { rid?: string | null; roomName?: string; title?: string; pjt?: { projectId?: string; cardId?: string; label?: string } | null; upload?: boolean }
export interface MeetMinutes { summary: string; decisions: string[]; actions: Array<{ who?: string; what: string; due?: string }>; topics?: string[] }
export interface MeetRecItem {
	id: string; title: string; owner: string; ownerName: string; mine: boolean; rid: string | null; roomName: string;
	pjt: { projectId?: string; cardId?: string; label?: string } | null; shares: string[];
	status: 'recording' | 'queued' | 'processing' | 'done' | 'failed'; startedAt: string; endedAt: string | null; durationSec: number;
	bytes: number; audio: boolean; audioExpiresAt: string | null; audioDeletedAt: string | null; minutes: MeetMinutes | null; transcript?: string; error: string;
	minutesLangs?: string[]; minutesByLang?: Record<string, MeetMinutes> | null;
	myNote?: string;   // 📝 내 비고(본인만 보임, 2026-10-27)
	// 🗣 대면 통역 기록(2026-10-28) — 'interp' 는 음성 없이 글만(양쪽 원문·번역). 목록에는 turns 가 빠져 온다
	kind?: 'rec' | 'interp';
	interp?: { me: string; them: string; turns: InterpTurnRec[] } | null;
}
export interface InterpTurnRec { side: 'me' | 'them'; src: string; srcLang: string; tx: string; txLang: string; at: string }

/** 회의록 언어 (2026-10-26 형 "각 언어별로도 하고 혼합도") — 표시 이름은 그 언어로(번역하지 않는다) */
export const MEET_REC_LANGS: Array<{ code: string; label: string }> = [
	{ code: 'ko', label: '한국어' }, { code: 'en', label: 'English' }, { code: 'vi', label: 'Tiếng Việt' }, { code: 'zh', label: '中文' }, { code: 'es', label: 'Español (México)' },
];

async function j<T>(res: Response): Promise<T> {
	const body = await res.json().catch(() => ({})) as T & { error?: string };
	if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
	return body;
}
const API = '/api/tpt/meetrec';
export const meetRecApi = {
	list: async () => (await j<{ items: MeetRecItem[] }>(await fetch(`${API}/list`))).items,
	get: async (id: string) => (await j<{ item: MeetRecItem }>(await fetch(`${API}/${encodeURIComponent(id)}`))).item,
	live: async (rid: string) => (await j<{ live: Array<{ id: string; title: string; ownerName: string; startedAt: string }> }>(await fetch(`${API}/live?rid=${encodeURIComponent(rid)}`))).live,
	update: async (id: string, body: { title?: string; shares?: string[] }) => j(await fetch(`${API}/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })),
	note: async (id: string, note: string) => (await j<{ note: string }>(await fetch(`${API}/${encodeURIComponent(id)}/note`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ note }) }))).note,
	remove: async (id: string) => j(await fetch(`${API}/${encodeURIComponent(id)}`, { method: 'DELETE' })),
	// 🗣 대면 통역 기록 — 같은 sid 면 서버가 덮어쓴다(차례마다 전체를 다시 보내도 안전)
	saveInterp: async (body: { sid: string; title: string; me: string; them: string; turns: InterpTurnRec[] }) => j<{ id: string }>(await fetch(`${API}/interp`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })),
	retry: async (id: string) => j(await fetch(`${API}/${encodeURIComponent(id)}/retry`, { method: 'POST' })),
	usage: async () => j<{ totals: { count: number; bytes: number; hours: number; liveBytes: number }; days: Array<{ day: string; count: number; bytes: number; hours: number }> }>(await fetch(`${API}/usage`)),
	/** 원본 재생용 — 인증 헤더가 필요해 blob 으로 받아 object URL 로 */
	audioUrl: async (id: string) => {
		const res = await fetch(`${API}/${encodeURIComponent(id)}/audio`);
		if (!res.ok) throw new Error((await res.json().catch(() => ({})) as { error?: string }).error || `HTTP ${res.status}`);
		return URL.createObjectURL(await res.blob());
	},
};

// ── 녹음 엔진 ─────────────────────────────────────────────
export type RecPhase = 'idle' | 'starting' | 'recording' | 'paused' | 'stopping';
export interface RecState { phase: RecPhase; id: string | null; title: string; rid: string | null; roomName: string; startedAt: number; pausedMs: number; pauseAt: number; pending: number; uploadError: string; error: string; native: boolean }
const INIT: RecState = { phase: 'idle', id: null, title: '', rid: null, roomName: '', startedAt: 0, pausedMs: 0, pauseAt: 0, pending: 0, uploadError: '', error: '', native: false };
let state: RecState = INIT;
const subs = new Set<() => void>();
const set = (p: Partial<RecState>) => { state = { ...state, ...p }; subs.forEach((f) => f()); };
export function useMeetRecState(): RecState {
	return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => state, () => state);
}
export function recElapsedSec(s: RecState, now = Date.now()): number {
	if (!s.startedAt) return 0;
	const paused = s.pausedMs + (s.phase === 'paused' && s.pauseAt ? now - s.pauseAt : 0);
	return Math.max(0, Math.round((now - s.startedAt - paused) / 1000));
}

let recorder: MediaRecorder | null = null;
let stream: MediaStream | null = null;
let seq = 0;
const queue: Array<{ seq: number; blob: Blob }> = [];
let pumping = false;
let pumpIdle: Array<() => void> = [];

function pickMime(): string {
	const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
	for (const m of cands) { try { if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m; } catch { /* */ } }
	return '';
}
type AndroidRec = { setRecording?: (on: boolean, title: string, rid: string) => void; meetRecStart?: (id: string, title: string, rid: string) => void; meetRecStop?: () => void; meetRecState?: () => string };

// 🍎 iOS 네이티브 녹음 (2026-10-27 맥 M-066 계약) — WKWebView 는 앱이 백그라운드·화면 잠금이면 웹 마이크를 끊어서, iOS 는 앱이 직접 녹음한다.
//   웹: 세션(/start, mime audio/aac) 생성 → TPTAndroid.meetRecStart(id,title,rid) → 앱이 30초 ADTS 조각을 /chunk?seq=N 로 올린다(로그인 헤더 포함)
//   → 끝내기 = meetRecStop() → 앱이 남은 조각을 다 올리고 {state:'done'} → 웹이 /stop. 진행 상황은 window 이벤트 'tpt:meetrec-native'.
//   전화·보이스톡이 끼면 앱이 멈췄다 이어 녹음(paused/resumed). 손으로 일시정지하는 브릿지는 없다(iOS 녹음 바엔 일시정지 버튼을 감춘다).
//   옛 iOS 빌드(브릿지 없음)는 녹음 메뉴를 숨긴다(meetRecRecordAvailable). ADTS 는 프레임마다 머리가 있어 서버가 바이트로 이어 붙여도 유효하다.
type NativeEv = { state: 'recording' | 'chunk' | 'paused' | 'resumed' | 'done' | 'error'; id?: string; seq?: number; ok?: boolean; reason?: string; chunks?: number };
export const MEET_REC_NATIVE_EVENT = 'tpt:meetrec-native';
export function nativeMeetRecAvailable(): boolean {
	if (!isIosApp()) return false;
	const b = (window as unknown as { TPTAndroid?: AndroidRec }).TPTAndroid;
	return !!b && typeof b.meetRecStart === 'function' && typeof b.meetRecStop === 'function';
}
let nativeOff: (() => void) | null = null;
let nativeDone: ((ok: boolean) => void) | null = null;
function listenNative(onFirst?: (e: NativeEv) => void): void {
	nativeOff?.();
	let first = true;
	const h = (ev: Event) => {
		const d = (ev as CustomEvent<NativeEv>).detail;
		if (!d || (d.id && state.id && d.id !== state.id)) return;
		if (first && (d.state === 'recording' || d.state === 'error')) { first = false; onFirst?.(d); }
		if (d.state === 'chunk') set({ uploadError: d.ok === false ? `chunk ${d.seq ?? ''}` : '' });
		else if (d.state === 'paused' && state.phase === 'recording') set({ phase: 'paused', pauseAt: Date.now() });
		else if (d.state === 'resumed' && state.phase === 'paused') set({ phase: 'recording', pausedMs: state.pausedMs + (Date.now() - state.pauseAt), pauseAt: 0 });
		else if (d.state === 'done') { const f = nativeDone; nativeDone = null; f?.(true); }
		else if (d.state === 'error' && !first) set({ uploadError: d.reason || 'error' });
	};
	window.addEventListener(MEET_REC_NATIVE_EVENT, h);
	nativeOff = () => { window.removeEventListener(MEET_REC_NATIVE_EVENT, h); nativeOff = null; };
}
async function startNative(o: { title: string; rid: string | null; shares: string[]; minutesLangs: string[]; pjt?: MeetRecStartReq['pjt']; lang: LangCode; announce: string }): Promise<void> {
	const b = androidRec()!;
	const created = await j<{ id: string }>(await fetch(`${API}/start`, {
		method: 'POST', headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ title: o.title, rid: o.rid, shares: o.shares, minutesLangs: o.minutesLangs, mime: 'audio/aac', pjt: o.pjt || null }),
	}));
	set({ id: created.id, native: true });
	const first = await new Promise<NativeEv>((resolve) => {
		const to = setTimeout(() => resolve({ state: 'error', reason: 'timeout' }), 15_000);
		listenNative((e) => { clearTimeout(to); resolve(e); });
		try { b.meetRecStart!.call(b, created.id, o.title, o.rid || ''); } catch { clearTimeout(to); resolve({ state: 'error', reason: 'bridge' }); }
	});
	if (first.state === 'error') {
		nativeOff?.();
		void meetRecApi.remove(created.id).catch(() => undefined);   // 시작 못 한 빈 세션 정리
		throw Object.assign(new Error(first.reason === 'mic-denied' ? 'NotAllowedError: mic-denied' : `native ${first.reason || 'error'}`), { name: first.reason === 'mic-denied' ? 'NotAllowedError' : 'Error' });
	}
	say(o.announce, o.lang);
	set({ phase: 'recording', startedAt: Date.now() });
	try { b.setRecording?.call(b, true, o.title, o.rid || ''); } catch { /* */ }
}
/** 웹이 새로 떴을 때(재로드) 앱이 녹음 중이면 녹음 바를 되살린다 — meetRecState() 동기 읽기 */
export function restoreNativeMeetRec(): void {
	if (!nativeMeetRecAvailable() || state.phase !== 'idle') return;
	const b = androidRec()!;
	let raw = '';
	try { raw = typeof b.meetRecState === 'function' ? String(b.meetRecState.call(b) || '') : ''; } catch { return; }
	if (!raw) return;
	try {
		const s0 = JSON.parse(raw) as { id?: string; state?: string; sec?: number; title?: string };
		if (!s0.id || (s0.state !== 'recording' && s0.state !== 'paused')) return;
		set({ ...INIT, phase: s0.state === 'paused' ? 'paused' : 'recording', id: s0.id, title: s0.title || '', native: true, startedAt: Date.now() - Math.max(0, Number(s0.sec) || 0) * 1000, pauseAt: s0.state === 'paused' ? Date.now() : 0 });
		listenNative();
	} catch { /* 깨진 값 무시 */ }
}
const androidRec = (): AndroidRec | null => ((window as unknown as { TPTAndroid?: AndroidRec }).TPTAndroid || null);

async function pump(): Promise<void> {
	if (pumping) return;
	pumping = true;
	try {
		while (queue.length) {
			const item = queue[0];
			let ok = false;
			for (let attempt = 0; attempt < 6 && !ok; attempt++) {
				try {
					const res = await fetch(`${API}/${encodeURIComponent(state.id || '')}/chunk?seq=${item.seq}`, { method: 'POST', headers: { 'content-type': 'application/octet-stream' }, body: item.blob });
					if (res.ok) ok = true;
					else if (res.status === 409 || res.status === 403 || res.status === 404) { ok = true; set({ uploadError: `HTTP ${res.status}` }); }   // 되돌릴 수 없는 거절 — 버린다
					else throw new Error(`HTTP ${res.status}`);
				} catch (e) {
					set({ uploadError: (e as Error).message });
					await new Promise((r) => setTimeout(r, Math.min(30_000, 2000 * 2 ** attempt)));
				}
			}
			if (!ok) break;   // 네트워크가 계속 안 되면 큐에 남겨 두고 다음 조각 때 다시 시도
			queue.shift();
			set({ pending: queue.length, uploadError: '' });
		}
	} finally {
		pumping = false;
		if (!queue.length) { const w = pumpIdle; pumpIdle = []; w.forEach((f) => f()); }
	}
}

function say(text: string, lang: LangCode) { try { speak(text, lang); } catch { /* 음성 안내 실패는 무시 */ } }

/** 녹음 시작 — 마이크 권한·서버 등록 실패는 throw */
export async function startMeetRecording(o: { title: string; rid: string | null; roomName?: string; shares: string[]; minutesLangs: string[]; pjt?: MeetRecStartReq['pjt']; lang: LangCode; announce: string }): Promise<void> {
	if (state.phase !== 'idle') throw new Error('이미 녹음 중입니다');
	if (!nativeMeetRecAvailable() && (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia)) throw new Error('이 기기에서는 녹음을 지원하지 않습니다');
	set({ ...INIT, phase: 'starting', title: o.title, rid: o.rid, roomName: o.roomName || '' });
	if (nativeMeetRecAvailable()) {
		try { await startNative(o); return; } catch (e) { set({ ...INIT, error: (e as Error).message }); throw e; }
	}
	try {
		stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: true, autoGainControl: true } });
		const mime = pickMime();
		const created = await j<{ id: string }>(await fetch(`${API}/start`, {
			method: 'POST', headers: { 'content-type': 'application/json' },
			body: JSON.stringify({ title: o.title, rid: o.rid, shares: o.shares, minutesLangs: o.minutesLangs, mime: mime || 'audio/webm', pjt: o.pjt || null }),
		}));
		seq = 0; queue.length = 0;
		recorder = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 32_000 });
		recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) { queue.push({ seq: seq++, blob: ev.data }); set({ pending: queue.length }); void pump(); } };
		say(o.announce, o.lang);
		recorder.start(30_000);
		set({ phase: 'recording', id: created.id, startedAt: Date.now() });
		try { androidRec()?.setRecording?.(true, o.title, o.rid || ''); } catch { /* 앱 구버전 */ }
		window.addEventListener('beforeunload', warnUnload);
	} catch (e) {
		cleanupStream();
		set({ ...INIT, error: (e as Error).message });
		throw e;
	}
}

function warnUnload(e: BeforeUnloadEvent) { if (state.phase !== 'idle') { e.preventDefault(); e.returnValue = ''; } }
function cleanupStream() {
	try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
	stream = null; recorder = null;
	try { androidRec()?.setRecording?.(false, '', ''); } catch { /* */ }
	window.removeEventListener('beforeunload', warnUnload);
}

export function pauseMeetRecording(): void {
	if (state.phase !== 'recording' || !recorder) return;
	try { recorder.pause(); set({ phase: 'paused', pauseAt: Date.now() }); } catch { /* */ }
}
export function resumeMeetRecording(): void {
	if (state.phase !== 'paused' || !recorder) return;
	try { recorder.resume(); set({ phase: 'recording', pausedMs: state.pausedMs + (Date.now() - state.pauseAt), pauseAt: 0 }); } catch { /* */ }
}

/** 종료 — 마지막 조각까지 올린 뒤 서버에 알린다. 반환 = 녹음 id */
export async function stopMeetRecording(o: { lang: LangCode; announce: string }): Promise<string | null> {
	if (state.phase !== 'recording' && state.phase !== 'paused') return null;
	const id = state.id;
	const durationSec = recElapsedSec(state);
	set({ phase: 'stopping' });
	if (state.native) {
		// 🍎 앱이 남은 조각을 다 올리면 {state:'done'} — 네트워크가 계속 안 되면 3분 뒤 있는 조각만으로 마무리
		const b = androidRec();
		await Promise.race([
			new Promise<void>((resolve) => { nativeDone = () => resolve(); try { b?.meetRecStop?.call(b); } catch { resolve(); } }),
			new Promise<void>((resolve) => setTimeout(resolve, 180_000)),
		]);
		nativeDone = null; nativeOff?.();
		try { b?.setRecording?.call(b, false, '', ''); } catch { /* */ }
		say(o.announce, o.lang);
		try { await j(await fetch(`${API}/${encodeURIComponent(id || '')}/stop`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ durationSec }) })); }
		finally { set({ ...INIT }); }
		return id;
	}
	const rec = recorder;
	if (rec && rec.state !== 'inactive') await new Promise<void>((resolve) => { rec.onstop = () => resolve(); try { rec.stop(); } catch { resolve(); } });
	cleanupStream();
	say(o.announce, o.lang);
	// 남은 조각 업로드 대기 — 네트워크가 계속 안 되면 2분 뒤 있는 조각만으로 마무리(서버는 받은 조각으로 회의록을 만든다)
	await Promise.race([
		new Promise<void>((resolve) => { if (!queue.length && !pumping) resolve(); else { pumpIdle.push(resolve); void pump(); } }),
		new Promise<void>((resolve) => setTimeout(resolve, 120_000)),
	]);
	try {
		await j(await fetch(`${API}/${encodeURIComponent(id || '')}/stop`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ durationSec }) }));
	} finally {
		set({ ...INIT });
	}
	return id;
}

// ── 녹음 파일 올려서 회의록 (2026-10-27 형 "녹음 파일 올려서 회의록 작성") ─────────────
//   녹음과 같은 길: start(source=upload) → 파일을 4MB 조각으로 chunk 순서대로 → stop. 서버 잡이 조각을 이어 원본 파일 그대로 되돌린다.
export const MEET_REC_UPLOAD_ACCEPT = 'audio/*,.mp3,.m4a,.wav,.aac,.ogg,.oga,.opus,.flac,.webm,.mp4,.wma';
export const MEET_REC_UPLOAD_MAX = 500 * 1024 * 1024;
const UP_SLICE = 4 * 1024 * 1024;

/** 파일 → 서버·Gemini 가 아는 오디오 mime (확장자 우선 — 윈도우는 m4a 를 audio/x-m4a, mp4 를 video/mp4 로 준다) */
export function uploadMimeOf(file: { name: string; type: string }): string {
	const ext = (file.name.split('.').pop() || '').toLowerCase();
	const byExt: Record<string, string> = { mp3: 'audio/mpeg', m4a: 'audio/mp4', mp4: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav', ogg: 'audio/ogg', oga: 'audio/ogg', opus: 'audio/ogg', flac: 'audio/flac', webm: 'audio/webm' };
	if (byExt[ext]) return byExt[ext];
	return /^audio\/[a-z0-9.+-]+$/i.test(file.type) ? file.type : '';
}

/** 오디오 길이(초) — 못 읽으면 0(webm 등은 Infinity 가 나와 0 으로) */
export function audioDurationSec(file: Blob): Promise<number> {
	return new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		const a = new Audio();
		const done = (v: number) => { URL.revokeObjectURL(url); resolve(Number.isFinite(v) && v > 0 ? Math.round(v) : 0); };
		const timer = setTimeout(() => done(0), 8000);
		a.preload = 'metadata';
		a.onloadedmetadata = () => { clearTimeout(timer); done(a.duration); };
		a.onerror = () => { clearTimeout(timer); done(0); };
		a.src = url;
	});
}

/** 파일 업로드 → 회의록 처리 대기열. 반환 = 회의록 id. onProgress(0~1) */
export async function uploadMeetRecFile(file: File, o: { title: string; rid: string | null; shares: string[]; minutesLangs: string[]; pjt?: MeetRecStartReq['pjt'] }, onProgress?: (p: number) => void): Promise<string> {
	const mime = uploadMimeOf(file);
	if (!mime) throw new Error('UNSUPPORTED');
	if (file.size > MEET_REC_UPLOAD_MAX) throw new Error('TOO_BIG');
	const durationSec = await audioDurationSec(file);
	const created = await j<{ id: string }>(await fetch(`${API}/start`, {
		method: 'POST', headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ title: o.title, rid: o.rid, shares: o.shares, minutesLangs: o.minutesLangs, mime, pjt: o.pjt || null, source: 'upload' }),
	}));
	const n = Math.max(1, Math.ceil(file.size / UP_SLICE));
	for (let i = 0; i < n; i++) {
		const blob = file.slice(i * UP_SLICE, Math.min(file.size, (i + 1) * UP_SLICE));
		let ok = false;
		for (let attempt = 0; attempt < 5 && !ok; attempt++) {
			try {
				const res = await fetch(`${API}/${encodeURIComponent(created.id)}/chunk?seq=${i}`, { method: 'POST', headers: { 'content-type': 'application/octet-stream' }, body: blob });
				if (res.ok) ok = true;
				else if (res.status >= 400 && res.status < 500) throw Object.assign(new Error((await res.json().catch(() => ({})) as { error?: string }).error || `HTTP ${res.status}`), { fatal: true });
				else throw new Error(`HTTP ${res.status}`);
			} catch (e) {
				if ((e as { fatal?: boolean }).fatal || attempt === 4) { void meetRecApi.remove(created.id).catch(() => undefined); throw e; }
				await new Promise((r) => setTimeout(r, 1500 * 2 ** attempt));
			}
		}
		onProgress?.((i + 1) / n);
	}
	await j(await fetch(`${API}/${encodeURIComponent(created.id)}/stop`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ durationSec }) }));
	return created.id;
}
